import { createRoute } from "@tanstack/solid-router";

import { rootRoute } from "../__root";
import { sessionActor } from "@/lib/session-machine";
import { accountsStore, readAccountsStateSync, readActiveAccountSync } from "@/lib/accounts";
import { ensureTabAccount } from "@/lib/active-account";
import { api, fetchApi } from "@/lib/api";
import { queryClient } from "@/lib/query-client";
import {
  cloneSession,
  type components,
  HTTP_FORBIDDEN,
  HTTP_UNAUTHORIZED,
  isErrorModel,
  isStatusError,
  isValidSession,
  queryKeys,
  type Session,
} from "@journal/shared";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { AppLayoutContent } from "@/features/layout/components/app-layout-content";
import { SESSION_CHECK_TIMEOUT_MS, SESSION_STALE_TIME_MS } from "@/lib/constants";

function AppLayout() {
  const { db } = usePowerSyncContext();

  // Render the static shell immediately. `db` may be null while PowerSync's
  // local SQLite is still initializing; AppLayoutContent and the data-bound
  // route components handle the null case with granular per-region skeletons,
  // so we never withhold the entire UI behind a single gate.
  return <AppLayoutContent db={db()} />;
}

// Deduplicates concurrent beforeLoad calls during the page-load preload burst.
// When viewport preloads fire simultaneously for multiple child routes, they all
// share a single API request instead of each sending their own.
let activeSessionCheck: Promise<void> | null = null;

const fetchSession = async () =>
  api.GET("/api/auth/get-session", { params: { query: { jwt: true } } });

async function runSessionCheck(
  snapshot: ReturnType<typeof sessionActor.getSnapshot>,
): Promise<void> {
  // Wait for the machine to finish priming from storage.
  if (snapshot.matches("priming")) {
    await new Promise<void>((resolve) => {
      const sub = sessionActor.subscribe((s) => {
        if (!s.matches("priming")) {
          sub.unsubscribe();
          resolve();
        }
      });
    });
  }

  // Multi-account: if the user explicitly chose the GUEST account, do NOT verify
  // with the server. The backend cookie may still authenticate a real account,
  // and pushing that into the machine here would flip the app back to that real
  // identity (and swap the active PowerSync DB away from the guest). Honor the
  // explicit guest choice; the real session is re-primed on switching back.
  if (readActiveAccountSync()?.kind === "guest") {
    return;
  }

  const currentSnapshot = sessionActor.getSnapshot();
  // Machine may have recovered a session from localStorage during priming.
  // If we have a session OR we have already verified with the server, we skip.
  if (currentSnapshot.context.session || currentSnapshot.context.isVerified) {
    return;
  }

  // No session in machine — verify with the server once.
  // The redirect is thrown outside try/catch so TanStack Router receives it.
  type FetchResult = Awaited<ReturnType<typeof fetchSession>>;

  const sessionResult = await (async () => {
    try {
      // Add a timeout to avoid hanging the route transition if the server is slow/rate-limited.
      // The value lives in `lib/constants` because the sibling `_auth` guard
      // needs EXACTLY the same ceiling (P23/CC-60 · I1-bis).
      const timeoutPromise = new Promise<FetchResult>((_resolve, reject) => {
        setTimeout(() => {
          reject(new Error("Session check timeout"));
        }, SESSION_CHECK_TIMEOUT_MS);
      });

      return await Promise.race([
        api.GET("/api/auth/get-session", {
          params: { query: { jwt: true } },
        }),
        timeoutPromise,
      ]);
    } catch (err) {
      console.warn("[SessionCheck] Error or timeout verifying session:", err);
      return null;
    }
  })();

  if (!sessionResult) {
    // Network error / timeout — DO NOT send VERIFY_COMPLETE. Marking the
    // machine as verified would lock the app in visitor mode permanently,
    // because every subsequent route load would early-return on `isVerified`
    // and the in-component `useSession` query is enabled by `!isPriming()`
    // alone. Leaving `isVerified: false` lets the next navigation retry
    // and `useSession` retry on mount.
    return;
  }

  const { data, error, response } = sessionResult;

  // Sync with machine regardless of success/error (error will clear session)
  let sessionPayload: Session | null = null;
  if (isValidSession(data)) {
    sessionPayload = cloneSession(data);
  }

  let errorPayload: components["schemas"]["ErrorModel"] | null = null;
  if (isErrorModel(error)) {
    errorPayload = error;
  }

  sessionActor.send({
    type: "SERVER_REPLY",
    session: sessionPayload,
    error: errorPayload,
  });

  if (error) {
    const status = isStatusError(error) ? error.status : response.status;
    if (status === HTTP_UNAUTHORIZED || status === HTTP_FORBIDDEN) {
      // Server explicitly rejected auth — allow access as a visitor/guest.
      // (SERVER_REPLY above already handled this, but we return early)
      return;
    }
    // Other server error — allow offline access.
    // Ensure we are marked as verified if the above SERVER_REPLY was skipped or didn't set it.
    // (Actually SERVER_REPLY always sets isVerified: true in our machine)
    return;
  }

  queryClient.setQueryData(queryKeys.auth.session(), data);
}

/**
 * Global search params for the `_app` shell (#15b). They cover global overlays
 * that do not belong to a specific route:
 *   - `palette` / `pq`: Command Palette open (+ preloaded term).
 *   - `shortcuts`: shortcut cheat-sheet open.
 * Because they live in the parent layout, every child page carries them
 * without losing them (TanStack keeps the parent's search params when navigating
 * between children), so opening the search is deep-linkable and survives reload/sharing.
 * `pq` (not `q`) is used to avoid colliding with `?q=` page searches
 * (fonts/language), which live in child routes.
 */
export interface AppShellSearch {
  palette?: boolean;
  pq?: string;
  shortcuts?: boolean;
  /** Account switcher overlay (URL-addressable via `?account=switch`). */
  account?: "switch";
}

// Accept the forms produced by TanStack's search parser when pasting a
// deep link: the boolean `true` (which we serialize), but also the
// NUMBER `1` (TanStack auto-parses `?palette=1` → number 1, not string) and the
// strings "1"/"true". Without the numeric case, pasting `?palette=1` into the bar
// evaluated to `undefined` → the router erased the param at boot and the overlay
// never opened (CC-18).
const asBool = (v: unknown): boolean | undefined =>
  v === true || v === 1 || v === "1" || v === "true" ? true : undefined;

export const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_app",
  validateSearch: (raw: Record<string, unknown>): AppShellSearch => ({
    palette: asBool(raw.palette),
    pq: typeof raw.pq === "string" && raw.pq !== "" ? raw.pq : undefined,
    shortcuts: asBool(raw.shortcuts),
    account: raw.account === "switch" ? "switch" : undefined,
  }),
  beforeLoad: () => {
    if (ensureTabAccount(readAccountsStateSync()) === null) {
      void accountsStore.ensureGuestActive();
    }

    const snapshot = sessionActor.getSnapshot();

    // Hot path: session already established and machine is done priming.
    // We only prefetch if the cached data is stale — useSession's createQuery
    // will handle refetching reactively once mounted.
    if (!snapshot.matches("priming") && snapshot.context.session) {
      const cached = queryClient.getQueryState(queryKeys.auth.session());
      const isStale =
        !cached?.dataUpdatedAt || Date.now() - cached.dataUpdatedAt > SESSION_STALE_TIME_MS;

      if (isStale) {
        void queryClient.prefetchQuery({
          queryKey: queryKeys.auth.session(),
          queryFn: async () =>
            fetchApi(api.GET("/api/auth/get-session", { params: { query: { jwt: true } } })),
          staleTime: SESSION_STALE_TIME_MS,
        });
      }
      return;
    }

    // Cold path: fire the verify in the background and let the route render
    // immediately. The InitialSyncOverlay covers the UI while the machine is
    // priming/verifying, and the AppLayout component reactively redirects to
    // /login if the verify resolves to an unauthenticated user. This matches
    // the behaviour of `apps/native`: routing is never gated on a network
    // round-trip, and the user never sees a frozen login form.
    activeSessionCheck ??= (async () => {
      try {
        await runSessionCheck(snapshot);
      } finally {
        activeSessionCheck = null;
      }
    })();
    // Intentional fire-and-forget — see comment above.
  },

  component: AppLayout,
});
